import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Landmark, ShieldCheck, KeyRound } from "lucide-react";

const AUTH_BG = "https://images.unsplash.com/photo-1551270029-4bc0f7eabf78";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, signup } = useAuth();
  const nav = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(username.trim(), password);
        toast.success("Welcome back to the Federal Reserve");
      } else {
        await signup({
          username: username.trim(),
          password,
          full_name: fullName.trim(),
          access_code: accessCode.trim(),
        });
        toast.success("Account established");
      }
      nav("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-5">
      {/* Left: Architectural imagery */}
      <div
        className="hidden lg:flex lg:col-span-2 relative bg-federal-navy grain-overlay overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(11,19,43,0.75), rgba(11,19,43,0.85)), url(${AUTH_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <Landmark className="w-7 h-7 text-institutional-gold" strokeWidth={1.5} />
            <span className="uppercase tracking-[0.3em] text-xs text-institutional-gold font-medium">
              Federal Reserve
            </span>
          </div>
          <div>
            <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] mb-6">
              A century of<br />
              institutional trust.
            </h1>
            <div className="h-px w-16 bg-institutional-gold mb-6"></div>
            <p className="text-white/70 max-w-md leading-relaxed">
              Sovereign-grade custody. Escrow, treasury, and multi-currency
              reserves — administered with the discipline of a central bank.
            </p>
          </div>
          <div className="flex items-center gap-4 text-white/50 text-xs uppercase tracking-widest">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            <span>FDIC-Simulated · Est. MCMLXXVII</span>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="lg:col-span-3 flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="lg:hidden flex items-center gap-2 mb-6 text-federal-navy">
              <Landmark className="w-6 h-6 text-institutional-gold" strokeWidth={1.5} />
              <span className="uppercase tracking-[0.3em] text-xs font-medium">
                Federal Reserve
              </span>
            </div>
            <div className="text-institutional-gold uppercase tracking-[0.25em] text-xs font-semibold mb-3">
              {mode === "login" ? "Client Portal" : "New Account"}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-federal-navy mb-3">
              {mode === "login" ? "Sign in" : "Create an account"}
            </h2>
            <p className="text-slate-600 text-sm">
              {mode === "login"
                ? "Access your escrow, checking and reserves."
                : "Create a new institutional account. Access code required."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            {mode === "signup" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-slate-700">
                  Full name
                </Label>
                <Input
                  data-testid="signup-fullname-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Alexander Hamilton"
                  className="rounded-none border-slate-300 bg-white h-12 text-base"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-slate-700">
                Username
              </Label>
              <Input
                data-testid="auth-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="a.hamilton"
                required
                className="rounded-none border-slate-300 bg-white h-12 text-base"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-slate-700">
                Password
              </Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="rounded-none border-slate-300 bg-white h-12 text-base"
              />
            </div>

            {mode === "signup" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-slate-700 flex items-center gap-2">
                  <KeyRound className="w-3.5 h-3.5 text-institutional-gold" strokeWidth={1.5} />
                  Access code
                </Label>
                <Input
                  data-testid="signup-access-code-input"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Required to create an account"
                  required
                  className="rounded-none border-slate-300 bg-white h-12 text-base font-mono"
                />
                <p className="text-xs text-slate-500 italic">
                  A special access code is required to open an account.
                </p>
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={submitting}
              className="w-full h-12 rounded-none bg-federal-navy hover:bg-federal-navy/90 text-white uppercase tracking-widest text-sm font-medium transition-colors"
            >
              {submitting
                ? "Verifying…"
                : mode === "login"
                ? "Sign in"
                : "Create account"}
            </Button>

            <div className="gold-divider my-8" />

            <div className="text-center text-sm text-slate-600">
              {mode === "login" ? (
                <>
                  Need to open an account?{" "}
                  <button
                    type="button"
                    data-testid="switch-to-signup-btn"
                    onClick={() => setMode("signup")}
                    className="text-federal-navy underline underline-offset-4 font-medium hover:text-institutional-gold transition-colors"
                  >
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already a client?{" "}
                  <button
                    type="button"
                    data-testid="switch-to-login-btn"
                    onClick={() => setMode("login")}
                    className="text-federal-navy underline underline-offset-4 font-medium hover:text-institutional-gold transition-colors"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
